import React, { useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Modal, Button, Spinner, Form } from "react-bootstrap";
import SignatureGenerator from "./SignatureGenerator";
import "./SignableDocument.scss";
import { BASE_URL } from "config/config";
import { useTranslation } from "react-i18next";
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const SignableDocument = ({ fileUrl, signatureData = {}, token }) => {
  const [numPages, setNumPages] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalId, setModalId] = useState(null);
  const [showGenerator, setShowGenerator] = useState(false);
  const [appliedSignatures, setAppliedSignatures] = useState({});
  const [chosenSignatures, setChosenSignatures] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [globalName, setGlobalName] = useState("");
  const [applyPrefixToAll, setApplyPrefixToAll] = useState(false);
  const containerRef = useRef(null);
  const { t } = useTranslation();
  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

  const getColorByType = (type) => {
    switch (type) {
      case "signature":
        return { border: "2px dashed blue", bg: "rgba(0,0,255,0.1)" };
      case "nomPrenom":
        return { border: "2px dashed green", bg: "rgba(0,255,0,0.1)" };
      case "prefix":
        return { border: "2px dashed orange", bg: "rgba(255,165,0,0.1)" };
      case "date":
        return { border: "2px dashed red", bg: "rgba(255,0,0,0.1)" };
      default:
        return { border: "2px dashed gray", bg: "rgba(128,128,128,0.1)" };
    }
  };

  const getPositionsForPage = (pageNumber) => {
    if (
      !signatureData.signatureFields ||
      !Array.isArray(signatureData.signatureFields)
    )
      return [];
    return signatureData.signatureFields
      .flatMap((field) =>
        (field.positions || []).map((pos) => ({
          ...pos,
          type: pos.type || "signature",
          name: field.recipientName || "Destinataire inconnu",
        }))
      )
      .filter((pos) => pos.page === pageNumber);
  };

  const handleFieldClick = (pos) => {
    setModalType(pos.type);
    setModalId(pos.id);

    if (pos.type === "signature") {
      if (chosenSignatures[token]) {
        setAppliedSignatures((prev) => ({
          ...prev,
          [pos.id]: chosenSignatures[token],
        }));
        return;
      }
      setShowModal(true);
      setShowGenerator(false);
      return;
    }

    if (pos.type === "date") {
      const today = new Date().toLocaleDateString("fr-FR");
      setAppliedSignatures((prev) => ({ ...prev, [pos.id]: today }));
      return;
    }

    if (globalName && (pos.type === "nomPrenom" || pos.type === "prefix")) {
      let value = globalName;
      if (pos.type === "prefix") {
        const parts = value.split(" ");
        value =
          parts.length >= 2
            ? parts[0][0].toUpperCase() + parts[1][0].toUpperCase()
            : value.slice(0, 2).toUpperCase();
      }
      setAppliedSignatures((prev) => ({ ...prev, [pos.id]: value }));
      return;
    }

    setShowModal(true);
  };

  const handleNameSubmit = () => {
    if (!nameInput.trim()) return;
    const name = nameInput.trim();
    setGlobalName(name);

    let value = name;
    if (modalType === "prefix") {
      const parts = name.split(" ");
      value =
        parts.length >= 2
          ? parts[0][0].toUpperCase() + parts[1][0].toUpperCase()
          : name.slice(0, 2).toUpperCase();

      if (applyPrefixToAll) {
        const newApplied = { ...appliedSignatures };
        signatureData.signatureFields.forEach((field) =>
          (field.positions || []).forEach((pos) => {
            if (pos.type === "prefix") {
              newApplied[pos.id] = value;
            }
          })
        );
        setAppliedSignatures(newApplied);
        setShowModal(false);
        setNameInput("");
        setApplyPrefixToAll(false);
        return;
      }
    }

    setAppliedSignatures((prev) => ({ ...prev, [modalId]: value }));
    setShowModal(false);
    setNameInput("");
    setApplyPrefixToAll(false);
  };

  const handleFileImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      // on stocke l'image et un ID unique pour le certificat
      const signature = { id: modalId, dataURL: reader.result };
      setChosenSignatures((prev) => ({ ...prev, [token]: signature }));
      setAppliedSignatures((prev) => ({ ...prev, [modalId]: signature }));
      setShowModal(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSelectGeneratedSignature = (signature) => {
    // signature = { id: uniqueId, dataURL }
    setChosenSignatures((prev) => ({ ...prev, [token]: signature }));
    setAppliedSignatures((prev) => ({ ...prev, [modalId]: signature }));
    setShowModal(false);
  };
  console.log("appliedSignatures", appliedSignatures);
  const handleApplySignatures = async () => {
    if (!token) {
      alert(t("signature.token"));
      return;
    }
    if (Object.keys(appliedSignatures).length === 0) {
      alert(t("signature.moinChamp"));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}signatures/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          valuesByFieldId: appliedSignatures,
        }),
      });

      if (!res.ok) throw new Error("Erreur serveur");

      const data = await res.json();
      console.log("Signatures appliquées:", data);
      setSuccess(true);
    } catch (err) {
      console.error(err);
      alert(t("signature.erreurSignature"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        border: "1px solid black",

        margin: "auto",
        position: "relative",

        width: 800,
        height: 800, // fixe la hauteur de l'affichage
        overflowY: "auto", // active scroll vertical
        overflowX: "hidden",
      }}
      ref={containerRef}>
      <Document
        file={fileUrl}
        loading="Chargement du PDF..."
        onLoadSuccess={onDocumentLoadSuccess}>
        {numPages &&
          Array.from(new Array(numPages), (_, index) => (
            <div key={index} style={{ position: "relative" }}>
              <Page
                pageNumber={index + 1}
                width={780}
                renderAnnotationLayer={false}
                renderTextLayer={false}
              />

              {getPositionsForPage(index + 1).map((pos, i) => {
                const colors = getColorByType(pos.type);
                const applied = appliedSignatures[pos.id];
                return (
                  <div
                    key={i}
                    style={{
                      position: "absolute",
                      left: pos.x,
                      top: pos.y,
                      width: pos.width,
                      height: pos.height,
                      border: applied ? "none" : colors.border,
                      backgroundColor: applied ? "transparent" : colors.bg,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                      fontWeight: "bold",
                      fontSize: 14,
                      textAlign: "center",
                    }}
                    onClick={() => handleFieldClick(pos)}>
                    {applied ? (
                      applied.dataURL ? (
                        <img
                          src={applied.dataURL}
                          alt="Signature"
                          style={{ maxWidth: "100%", maxHeight: "100%" }}
                        />
                      ) : (
                        applied
                      )
                    ) : (
                      pos.type.toUpperCase()
                    )}
                  </div>
                );
              })}
            </div>
          ))}
      </Document>

      {Object.keys(appliedSignatures).length > 0 && (
        <div style={{ marginTop: 20, textAlign: "center" }}>
          <Button
            variant="success"
            style={{ borderRadius: "2px", marginBottom: "10px" }}
            onClick={handleApplySignatures}
            disabled={loading || success}>
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : success ? (
              <>{t("signature.applique") + " ✅"}</>
            ) : (
              t("signature.appliquer")
            )}
          </Button>
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        className="modal-sign"
        size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <p className="title-modal-sign">
              {" "}
              {modalType === "signature"
                ? t("signature.choisirSign")
                : t("signature.votreNom")}
            </p>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalType === "signature" ? (
            showGenerator ? (
              <SignatureGenerator
                onSelectSignature={handleSelectGeneratedSignature}
              />
            ) : (
              <div
                style={{ display: "flex", flexDirection: "column", gap: 15 }}>
                <Button className="btn-choix" as="label">
                  {t("signature.import")}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleFileImport}
                  />
                </Button>
                <Button
                  className="btn-sign"
                  onClick={() => setShowGenerator(true)}>
                  {t("signature.genererSign")}
                </Button>
              </div>
            )
          ) : (
            <Form>
              <Form.Group>
                <Form.Label>{t("signature.nom")}</Form.Label>
                <Form.Control
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                />
              </Form.Group>
              {modalType === "prefix" && (
                <Form.Check
                  type="checkbox"
                  label={t("signature.check")}
                  checked={applyPrefixToAll}
                  onChange={(e) => setApplyPrefixToAll(e.target.checked)}
                  style={{ marginTop: 10 }}
                />
              )}
              <Button
                className="btn-sign"
                style={{ marginTop: 15, width: 200 }}
                onClick={handleNameSubmit}>
                {t("signature.valid")}
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SignableDocument;
